import { useLanguage } from '@/i18n/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import SocialButton from './SocialButton';
import celloScroll from '@/assets/cello-scroll.webp';

const HeroSection = () => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();

  const instagramUrl = settings.social_instagram || 'https://www.instagram.com/szoradi.instruments/';
  const facebookUrl = settings.social_facebook || 'https://www.facebook.com/Szoradi.Instruments/';

  return (
    <section 
      className="min-h-screen flex flex-col items-center justify-center px-6 py-24 relative overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* Subtle background gradient */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-background via-background to-secondary/20 pointer-events-none"
        aria-hidden="true"
      />

      {/* Background decorative image */}
      <div 
        className="absolute right-0 top-1/2 -translate-y-1/2 w-2/5 max-w-lg h-[80vh] opacity-[0.08] pointer-events-none hidden lg:block"
        aria-hidden="true"
      >
        <img src={celloScroll} alt="" className="w-full h-full object-cover object-center grayscale" width={800} height={1122} />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-background" />
      </div>

      <div className="max-w-3xl mx-auto text-center relative z-10">
        <h1 id="hero-heading" className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-foreground tracking-wide animate-fade-up">
          {t.brandName}
        </h1>

        <div className="w-20 h-px bg-gold mx-auto mt-10 mb-10 animate-fade-up-delay-1" aria-hidden="true" />

        <p className="font-serif text-lg sm:text-xl md:text-2xl text-foreground/90 leading-relaxed text-balance animate-fade-up-delay-1 max-w-2xl mx-auto">
          {t.tagline}
        </p>

        <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto animate-fade-up-delay-2">
          {t.subline}
        </p>

        <div className="my-16 animate-fade-up-delay-2">
          <div className="relative inline-block group">
            <div className="absolute -inset-4 border border-gold/20 transition-all duration-500 group-hover:border-gold/40" aria-hidden="true" />
            <div className="absolute -inset-1 border border-primary/10" aria-hidden="true" />
            <img 
              src={celloScroll} 
              alt="Handcrafted cello scroll showing exceptional craftsmanship by Szoradi Instruments" 
              className="w-60 sm:w-72 h-auto relative transition-transform duration-700 ease-out group-hover:scale-[1.02]"
              width={800}
              height={1122}
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
          </div>
          <p className="mt-8 text-xs tracking-[0.25em] uppercase text-muted-foreground/60 font-sans">
            {t.imageCaption}
          </p>
        </div>

        <div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-10 animate-fade-up-delay-3"
          role="navigation"
          aria-label="Social media links"
        >
          {instagramUrl && <SocialButton platform="instagram" href={instagramUrl} />}
          {facebookUrl && <SocialButton platform="facebook" href={facebookUrl} />}
        </div>

        <p className="mt-12 text-sm text-muted-foreground/70 max-w-md mx-auto leading-relaxed animate-fade-up-delay-4">
          {t.followText}
        </p>
      </div>
    </section>
  );
};

export default HeroSection;
